import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import MembersOnlyTicketTierGroup from '@/components/MembersOnlyTierGroup';
import TicketTier from '@/components/TicketTier';
import type { UserMembership, UserProfile } from '@/src/api/authApi';
import type {
  MembershipPlan,
  MembershipTier,
  PSPrice,
  ShortParticipationRequest,
  TierBookability,
} from '@/src/api/eventsApi';
import type { Membership } from '@/src/api/membershipsApi';
import {
  ACCESS_PASS_PLAN,
  heldMembership,
  PREVIEW_CATALOG,
  previewSession,
  TRIAL_PLAN,
} from '@/src/lib/membershipFixtures';
import { derivePlanToUpsellInMembersOnlyTicketTierGroup } from '@/src/lib/membershipUpsell';

/**
 * ── /components/ticket-tier ──────────────────────────────────────────────
 *
 * TicketTier — one purchasable ticket option in the event page's "Available
 * tickets" list (`app/events/[id]/index.tsx`, inside the `isTicketable &&
 * selectedSlot` block). The card is RENDER-ONLY: the screen hands it the
 * server's `bookability` verdict (`TierBookability`, `src/api/eventsApi.ts`)
 * verbatim, and the card just draws whichever of the two inline top-right
 * treatments (stepper / "+ Add" / "Sold out" badge) or foot `TierNotice`
 * that reason implies. It used to own the gating decision itself (a
 * 3-boolean ladder); it doesn't any more (see the component's own
 * docstring and `docs/building-components.md`'s note on its history) — so
 * this preview is organised the same way the component now thinks: one
 * entry per `TierBookability` reason, not per prop combination.
 *
 * `TierBlockedReason` has EIGHT members. `slot_in_past` is deliberately NOT
 * reproduced here: the event screen drops a slot's tiers outright once the
 * slot is in the past (see `TicketTier`'s own `renderNotice`, whose
 * `slot_in_past` case returns null with the comment "a past slot never
 * reaches a card at all") — so a card showing that reason is not a real
 * state. The other seven blocked reasons all DO reach a real card and are
 * every one covered below, alongside three flavours of bookable.
 *
 * Quantity is LOCAL state here (`tierQty` + `changeTierQty`), mirroring
 * `app/events/[id]/index.tsx`'s own `eventCart`: the card only reports the
 * +1/-1 intent, the caller clamps and stores. This file's copy is
 * self-contained — it does not import the old `all.tsx` holding-pen's
 * version of the same state.
 * ─────────────────────────────────────────────────────────────────────────
 */

// An ISO datetime N days out, at a fixed evening time — used below to build a
// `limit_reached` reset date that always reads as a future date regardless of
// the run date. Local-time ISO with no trailing Z (parseISO reads it as
// local, see the dateFormatter note) — toISOString would shift to UTC, so
// this is built by hand rather than reaching for that.
const slotIso = (daysOut: number, hour = 19) => {
  const d = new Date();
  d.setDate(d.getDate() + daysOut);
  d.setHours(hour, 30, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:30:00`;
};

// The memberships allowed to book a gated tier — one badge each. Three of
// them is the case worth eyeballing: the names are long enough that the
// badge row wraps onto its own extra line.
const TRIAL_PASS: MembershipTier = {
  id: 'm-trial',
  kind: 'trial',
  name: 'Trial Pass',
};
const ACCESS_PASS: MembershipTier = {
  id: 'm-access',
  kind: 'access_pass',
  name: 'Access Pass',
};
const ACCESS_PASS_PREMIUM: MembershipTier = {
  id: 'm-premium',
  kind: 'access_pass',
  name: 'Access Pass Premium',
};

const PREVIEW_PLAN: MembershipPlan = {
  id: 'plan-premium',
  name: 'Access Pass Premium',
  description:
    'The Access Pass — your membership to Putting Scene, billed monthly.',
  priceWithTax: { amountInMinorUnits: 10000, currency: 'INR', display: '₹100' },
};
// Relative to now (like slotIso above), so the "resets on …" line in the
// limit_reached previews always reads as a future date.
const PREVIEW_RESET_AT = slotIso(21);

type TierPreview = {
  id: string;
  label: string;
  name: string;
  price: PSPrice;
  description: string;
  // Spelled out on every entry, `null` included: "anyone can book" is a
  // state worth reading in the fixture, not an omission — null and []
  // mean opposite things upstream (see TicketTier's docstring).
  membershipsAppliedToTickettier: MembershipTier[] | null;
  bookability: TierBookability;
  maxPerUser: number;
  // Spelled out on every entry too: it's what blanks the Add/stepper slot
  // even while bookable, so it's worth reading per case.
  requiresGuestApproval: boolean;
  // Only non-null on the two approval-only states that need it —
  // `booking_confirmed_cannot_reapply` reads its `bookingId` off THIS, not
  // off the reason (see ShortParticipationRequest's docstring).
  participationRequest: ShortParticipationRequest | null;
};

const PREVIEW_TIERS: TierPreview[] = [
  // ── Bookable ──────────────────────────────────────────────────────────
  {
    id: 'bookable',
    label:
      'Bookable, ungated (memberships null) — stepper, and no badge row at all',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: null,
    bookability: { isBookable: true, reason: null },
    maxPerUser: 6,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'bookable-free',
    label: 'Bookable, free — the server\'s own "Free" display string',
    name: 'Community Walk-in',
    price: { amountInMinorUnits: 0, currency: 'INR', display: 'Free' },
    description: 'Standing room, first-come seating',
    membershipsAppliedToTickettier: null,
    bookability: { isBookable: true, reason: null },
    maxPerUser: 4,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'bookable-ap',
    label: 'Bookable + Access Pass badge — a pass-holder at a gated tier',
    name: "Chef's Counter",
    price: { amountInMinorUnits: 450000, currency: 'INR', display: '₹4,500' },
    description: 'Reserved counter + two off-menu courses + a cellar pour',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: { isBookable: true, reason: null },
    maxPerUser: 2,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'bookable-many-memberships',
    label:
      'Three allowed memberships — the badge row wraps past the price (a member who holds one of them)',
    name: "Chef's Counter",
    price: { amountInMinorUnits: 450000, currency: 'INR', display: '₹4,500' },
    description: 'Reserved counter + two off-menu courses + a cellar pour',
    membershipsAppliedToTickettier: [
      TRIAL_PASS,
      ACCESS_PASS,
      ACCESS_PASS_PREMIUM,
    ],
    bookability: { isBookable: true, reason: null },
    maxPerUser: 2,
    requiresGuestApproval: false,
    participationRequest: null,
  },

  // ── sold_out — the ONLY reason that mutes the card ──────────────────────
  {
    id: 'soldout-many-memberships',
    label:
      'Three memberships + sold_out — every badge dims, "Sold out" does not',
    name: "Chef's Counter",
    price: { amountInMinorUnits: 450000, currency: 'INR', display: '₹4,500' },
    description: 'Reserved counter + two off-menu courses + a cellar pour',
    membershipsAppliedToTickettier: [
      TRIAL_PASS,
      ACCESS_PASS,
      ACCESS_PASS_PREMIUM,
    ],
    bookability: { isBookable: false, reason: { type: 'sold_out' } },
    maxPerUser: 2,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'sold-out',
    label:
      'sold_out (AP tier) — badge sits inline where the stepper would, AP badge dims, description dims via opacity (not per-node token, see the docstring)',
    name: 'Early Bird',
    price: { amountInMinorUnits: 180000, currency: 'INR', display: '₹1,800' },
    description: 'Six tasting pours + small plates',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: { isBookable: false, reason: { type: 'sold_out' } },
    maxPerUser: 4,
    requiresGuestApproval: false,
    participationRequest: null,
  },

  // ── unauthenticated ──────────────────────────────────────────────────────
  {
    id: 'unauthenticated',
    label:
      'unauthenticated — arrives on EVERY tier when logged out, so a 3-tier event shows this notice 3×; no CTA (login lives elsewhere)',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: null,
    bookability: { isBookable: false, reason: { type: 'unauthenticated' } },
    maxPerUser: 6,
    requiresGuestApproval: false,
    participationRequest: null,
  },

  // ── max_seats_per_user_exhausted ─────────────────────────────────────────
  {
    id: 'seats-one-booking',
    label:
      'max_seats_per_user_exhausted, ONE booking — CTA reads "View booking"',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: null,
    bookability: {
      isBookable: false,
      reason: {
        type: 'max_seats_per_user_exhausted',
        usage: { limit: 2, used: 2 },
        bookings: [{ id: 'bk-1' }],
      },
    },
    maxPerUser: 2,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'seats-many-bookings',
    label: 'max_seats_per_user_exhausted, SEVERAL bookings — CTA pluralises',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: null,
    bookability: {
      isBookable: false,
      reason: {
        type: 'max_seats_per_user_exhausted',
        usage: { limit: 3, used: 3 },
        bookings: [{ id: 'bk-1' }, { id: 'bk-2' }],
      },
    },
    maxPerUser: 3,
    requiresGuestApproval: false,
    participationRequest: null,
  },

  // ── membership_required ──────────────────────────────────────────────────
  {
    id: 'membership-required',
    label:
      "membership_required — title/body/price are the PLAN's own copy, not a house pitch (the plan could be any of several)",
    name: "Chef's Counter",
    price: { amountInMinorUnits: 450000, currency: 'INR', display: '₹4,500' },
    description: 'Reserved counter + two off-menu courses + a cellar pour',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: {
      isBookable: false,
      reason: {
        type: 'membership_required',
        membershipsRequiredToBook: [PREVIEW_PLAN],
      },
    },
    maxPerUser: 2,
    requiresGuestApproval: false,
    participationRequest: null,
  },

  // ── limit_reached — up to two independent sentences, both can be absent ─
  {
    id: 'limit-reset-upgrade',
    label:
      'limit_reached — renewing plan + an upgrade available (both sentences)',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: {
      isBookable: false,
      reason: {
        type: 'limit_reached',
        usage: {
          label: 'PS Original',
          periodResetsAt: PREVIEW_RESET_AT,
          limit: 2,
          used: 2,
        },
        membershipsRequiredToBook: [PREVIEW_PLAN],
      },
    },
    maxPerUser: 4,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'limit-noreset-upgrade',
    label:
      'limit_reached — no reset (trial pass / cancelled), upgrade available: upgrade sentence only',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: {
      isBookable: false,
      reason: {
        type: 'limit_reached',
        usage: {
          label: 'PS Original',
          periodResetsAt: null,
          limit: 1,
          used: 1,
        },
        membershipsRequiredToBook: [PREVIEW_PLAN],
      },
    },
    maxPerUser: 4,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'limit-reset-topplan',
    label:
      'limit_reached — top plan, but it renews: reset sentence only, NO button',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: {
      isBookable: false,
      reason: {
        type: 'limit_reached',
        usage: {
          label: 'PS Original',
          periodResetsAt: PREVIEW_RESET_AT,
          limit: 2,
          used: 2,
        },
        membershipsRequiredToBook: null,
      },
    },
    maxPerUser: 4,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: 'limit-deadend',
    label:
      'limit_reached — top plan AND no reset: the genuine dead end (fallback sentence, no button) — needs a product answer',
    name: 'General Admission',
    price: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
    description: '7-course tasting menu + welcome drink',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: {
      isBookable: false,
      reason: {
        type: 'limit_reached',
        usage: {
          label: 'PS Original',
          periodResetsAt: null,
          limit: 2,
          used: 2,
        },
        membershipsRequiredToBook: null,
      },
    },
    maxPerUser: 4,
    requiresGuestApproval: false,
    participationRequest: null,
  },

  // ── requiresGuestApproval — caps at one seat, skips Add/stepper entirely ─
  {
    id: 'approval-only-bookable',
    label:
      'requiresGuestApproval, bookable — eventCart derives it onto the cart the moment its slot is picked, so the action slot is empty (no Add, no stepper)',
    name: 'Guest List',
    price: { amountInMinorUnits: 0, currency: 'INR', display: 'Free' },
    description: 'One spot, subject to the host approving your request',
    membershipsAppliedToTickettier: null,
    bookability: { isBookable: true, reason: null },
    maxPerUser: 1,
    requiresGuestApproval: true,
    participationRequest: null,
  },
  {
    id: 'approval-only-pending',
    label:
      'request_pending_approval — an existing request from a prior visit; own foot notice, no action',
    name: 'Guest List',
    price: { amountInMinorUnits: 0, currency: 'INR', display: 'Free' },
    description: 'One spot, subject to the host approving your request',
    membershipsAppliedToTickettier: null,
    bookability: {
      isBookable: false,
      reason: {
        type: 'request_pending_approval',
        request: { id: 'req-1' },
      },
    },
    maxPerUser: 1,
    requiresGuestApproval: true,
    participationRequest: {
      id: 'req-1',
      status: 'pending',
      approvalQueries: { socialLink: 'https://instagram.com/handle' },
      bookingId: null,
      createdAt: slotIso(-2),
    },
  },
  {
    id: 'approval-only-approved',
    label:
      'booking_confirmed_cannot_reapply — request approved; notice links straight to the booking (via participationRequest.bookingId), not the (now moot) request',
    name: 'Guest List',
    price: { amountInMinorUnits: 0, currency: 'INR', display: 'Free' },
    description: 'One spot, subject to the host approving your request',
    membershipsAppliedToTickettier: null,
    bookability: {
      isBookable: false,
      reason: {
        type: 'booking_confirmed_cannot_reapply',
        request: { id: 'req-1' },
      },
    },
    maxPerUser: 1,
    requiresGuestApproval: true,
    participationRequest: {
      id: 'req-1',
      status: 'approved',
      approvalQueries: { socialLink: 'https://instagram.com/handle' },
      bookingId: 'booking-1',
      createdAt: slotIso(-5),
    },
  },
];

// Every reason again, on the members-only band. Ungated fixtures get an Access
// Pass gate so each blocked reason can be eyeballed on the dark surface.
const MEMBERS_ONLY_TIERS: TierPreview[] = PREVIEW_TIERS.map((tier) => ({
  ...tier,
  id: `members-${tier.id}`,
  membershipsAppliedToTickettier: tier.membershipsAppliedToTickettier ?? [
    ACCESS_PASS,
  ],
}));

// ── Members-only scenarios ────────────────────────────────────────────────
// One event, two gated tiers (Trial + AP, and AP-only), per member state. The
// plan card under them comes from the live `deriveMembersOnlyPlan`; the tiers'
// bookability is hand-written to match what the server would send that user.

const toBuy = (plan: Membership): MembershipPlan => ({
  id: plan.id,
  name: plan.name,
  description: plan.description,
  priceWithTax: plan.priceWithTax,
});
const TRIAL_TO_BUY = toBuy(TRIAL_PLAN);
const AP_TO_BUY = toBuy(ACCESS_PASS_PLAN);

const needs = (...plans: MembershipPlan[]): TierBookability => ({
  isBookable: false,
  reason: { type: 'membership_required', membershipsRequiredToBook: plans },
});
const BOOKABLE: TierBookability = { isBookable: true, reason: null };
const LOGGED_OUT: TierBookability = {
  isBookable: false,
  reason: { type: 'unauthenticated' },
};

const usedUpTrial: UserMembership = {
  ...heldMembership('trial', 'Trial Pass'),
  perksUsage: {
    resetsAt: null,
    perks: {
      normal: {
        label: 'Events',
        bookings: 2,
        canBook: false,
        isUnlimited: false,
        limit: 2,
        remaining: 0,
      },
      ps_original: {
        label: 'PS Original',
        bookings: 0,
        canBook: false,
        isUnlimited: false,
        limit: 0,
        remaining: 0,
      },
    },
  },
};

const scenarioTiers = (
  scenarioId: string,
  vip: TierBookability,
  superFan: TierBookability
): TierPreview[] => [
  {
    id: `${scenarioId}-vip`,
    label: '',
    name: 'VIP Admission',
    price: { amountInMinorUnits: 700000, currency: 'INR', display: '₹7,000' },
    description:
      'What you get:\n- A welcome drink on the house\n- One piece of official merch included',
    membershipsAppliedToTickettier: [ACCESS_PASS, TRIAL_PASS],
    bookability: vip,
    maxPerUser: 2,
    requiresGuestApproval: false,
    participationRequest: null,
  },
  {
    id: `${scenarioId}-superfan`,
    label: '',
    name: 'Super Fan Admission',
    price: {
      amountInMinorUnits: 1000000,
      currency: 'INR',
      display: '₹10,000',
    },
    description:
      'What you get:\n- A welcome drink on the house\n- One piece of official merch included\n- Meet & Greet Access\n- Fan box priority entry',
    membershipsAppliedToTickettier: [ACCESS_PASS],
    bookability: superFan,
    maxPerUser: 2,
    requiresGuestApproval: false,
    participationRequest: null,
  },
];

const MEMBERS_ONLY_SCENARIOS: {
  id: string;
  label: string;
  session: { user: UserProfile | null; pastMemberships: UserMembership[] };
  upsellPlanHeaderText?: string;
  tiers: TierPreview[];
}[] = [
  {
    id: 'new',
    label: '1. No pass, never bought one — Trial Pass card',
    session: previewSession(null),
    upsellPlanHeaderText: 'Try it before you buy it',
    tiers: scenarioTiers(
      'new',
      needs(TRIAL_TO_BUY, AP_TO_BUY),
      needs(AP_TO_BUY)
    ),
  },
  {
    id: 'trial-ended',
    label: '2a. No pass, had the Trial Pass — Access Pass card',
    session: previewSession(null, [heldMembership('trial', 'Trial Pass')]),
    tiers: scenarioTiers('trial-ended', needs(AP_TO_BUY), needs(AP_TO_BUY)),
  },
  {
    id: 'ex-member',
    label: '2b. No pass, had an Access Pass — Access Pass card',
    session: previewSession(null, [
      { ...heldMembership('access_pass', 'Access Pass'), phase: 'past' },
    ]),
    tiers: scenarioTiers('ex-member', needs(AP_TO_BUY), needs(AP_TO_BUY)),
  },
  {
    id: 'trial-used-up',
    label: '3. On the Trial Pass, every perk used up — Access Pass card',
    session: previewSession(usedUpTrial),
    tiers: scenarioTiers(
      'trial-used-up',
      {
        isBookable: false,
        reason: {
          type: 'limit_reached',
          usage: {
            label: 'Events',
            periodResetsAt: null,
            limit: 2,
            used: 2,
          },
          membershipsRequiredToBook: [AP_TO_BUY],
        },
      },
      needs(AP_TO_BUY)
    ),
  },
  {
    id: 'on-trial',
    label: 'On the Trial Pass, perks left — no card, tiers handle it',
    session: previewSession(heldMembership('trial', 'Trial Pass')),
    tiers: scenarioTiers('on-trial', BOOKABLE, needs(AP_TO_BUY)),
  },
  {
    id: 'member',
    label: 'On the Access Pass — no card',
    session: previewSession(heldMembership('access_pass', 'Access Pass')),
    tiers: scenarioTiers('member', BOOKABLE, BOOKABLE),
  },
  {
    id: 'signed-out',
    label: 'Signed out — no card, every tier says log in',
    session: { user: null, pastMemberships: [] },
    tiers: scenarioTiers('signed-out', LOGGED_OUT, LOGGED_OUT),
  },
];

export default function TicketTierPreview() {
  // Mirrors app/events/[id]/index.tsx's eventCart: TicketTier only reports
  // the +1/-1 intent via onIncrement/onDecrement, the caller clamps and
  // stores the resulting quantity.
  const [tierQty, setTierQty] = useState<Record<string, number>>({});
  const changeTierQty = (id: string, maxPerUser: number, delta: number) =>
    setTierQty((prev) => ({
      ...prev,
      [id]: Math.max(0, Math.min(maxPerUser, (prev[id] ?? 0) + delta)),
    }));

  const renderTier = (tier: TierPreview, surface: 'light' | 'dark') => (
    <TicketTier
      key={tier.id}
      name={tier.name}
      price={tier.price}
      description={tier.description}
      membershipsAppliedToTickettier={tier.membershipsAppliedToTickettier}
      bookability={tier.bookability}
      requiresGuestApproval={tier.requiresGuestApproval}
      participationRequest={tier.participationRequest}
      quantity={tierQty[tier.id] ?? 0}
      numTickets={tier.maxPerUser}
      onDecrement={() => changeTierQty(tier.id, tier.maxPerUser, -1)}
      onIncrement={() => changeTierQty(tier.id, tier.maxPerUser, 1)}
      onViewBookings={() => {}}
      surface={surface}
    />
  );

  const caption = (label: string, surface: 'light' | 'dark') => (
    <Text
      fontFamily="$body"
      fontSize="$small"
      color={surface === 'dark' ? '$textWhiteMuted' : '$textDarkMuted'}
    >
      {label}
    </Text>
  );

  return (
    <>
      <Head>
        <title>TicketTier · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* The card is render-only: it draws the `bookability` it's handed.
            Cart qty is live here; notice CTAs are no-ops. */}
          {PREVIEW_TIERS.map((tier) => (
            <YStack key={tier.id} gap="$xs" alignSelf="stretch">
              {caption(tier.label, 'light')}
              {renderTier(tier, 'light')}
            </YStack>
          ))}

          {MEMBERS_ONLY_SCENARIOS.map((scenario) => (
            <YStack key={scenario.id} gap="$xs" alignSelf="stretch">
              {caption(scenario.label, 'light')}
              <MembersOnlyTicketTierGroup
                upsellPlanHeaderText={scenario.upsellPlanHeaderText}
                upsellPlan={derivePlanToUpsellInMembersOnlyTicketTierGroup({
                  ...scenario.session,
                  plans: PREVIEW_CATALOG,
                  hasNoTrialTicketTiers: !scenario.tiers.some((tier) =>
                    tier.membershipsAppliedToTickettier?.some(
                      (membership) => membership.kind === 'trial'
                    )
                  ),
                })}
                onSelectPlan={() => {}}
              >
                {scenario.tiers.map((tier) => renderTier(tier, 'dark'))}
              </MembersOnlyTicketTierGroup>
            </YStack>
          ))}

          {caption('Every reason, on the dark surface', 'light')}
          <MembersOnlyTicketTierGroup upsellPlan={null} onSelectPlan={() => {}}>
            {MEMBERS_ONLY_TIERS.map((tier) => (
              <YStack key={tier.id} gap="$xs" alignSelf="stretch">
                {caption(tier.label, 'dark')}
                {renderTier(tier, 'dark')}
              </YStack>
            ))}
          </MembersOnlyTicketTierGroup>
        </YStack>
      </ScrollView>
    </>
  );
}
